import React from 'react';
import { Info, ActivitySquare, Heart, FileText, Users, Bell, BarChart } from 'lucide-react';

/**
 * Компонент с информацией о WHR (соотношении талия-бедра)
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-emerald-500" />
        Всё о соотношении талия-бедра (WHR): значение, нормы и рекомендации
      </h2>
      
      <div className="prose prose-emerald dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <ActivitySquare className="w-5 h-5 text-emerald-500" />
          Что такое индекс соотношения талии к бедрам (WHR)?
        </h3>
        <p>
          <strong>Индекс соотношения талии к бедрам (WHR)</strong> — это антропометрический показатель, который рассчитывается делением окружности талии на окружность бедер. Данный индекс используется для оценки распределения жировой ткани в организме человека и связанных с этим рисков для здоровья.
        </p>
        <p>
          WHR является важным показателем, так как тип распределения жира в организме может быть более значимым фактором риска развития ряда заболеваний, чем просто избыточный вес. Абдоминальное (центральное) ожирение, когда жир накапливается преимущественно в области живота, связано с более высоким риском сердечно-сосудистых заболеваний, диабета 2 типа и других метаболических нарушений.
        </p>

        <h3 className="flex items-center gap-2">
          <Heart className="w-5 h-5 text-emerald-500" />
          Почему WHR важен для здоровья?
        </h3>
        <p>
          Индекс WHR позволяет выявить тип ожирения и оценить связанные с ним риски для здоровья:
        </p>
        <ul>
          <li>
            <strong>Абдоминальный (центральный) тип ожирения</strong> — характеризуется накоплением жира преимущественно в области живота и внутренних органов (висцеральный жир). Люди с таким типом ожирения имеют фигуру по типу «яблока». Данный тип ассоциируется с повышенным риском развития:
            <ul>
              <li>Сердечно-сосудистых заболеваний</li>
              <li>Сахарного диабета 2 типа</li>
              <li>Артериальной гипертензии</li>
              <li>Метаболического синдрома</li>
              <li>Некоторых видов рака</li>
              <li>Жировой болезни печени</li>
            </ul>
          </li>
          <li>
            <strong>Гиноидный (периферический) тип ожирения</strong> — характеризуется отложением жира преимущественно в области бедер и ягодиц. Люди с таким типом ожирения имеют фигуру по типу «груши». Данный тип считается менее опасным для здоровья.
          </li>
        </ul>

        <h3 className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-emerald-500" />
          Нормативные значения WHR в России
        </h3>
        <p>
          В соответствии с рекомендациями Министерства здравоохранения Российской Федерации и Российского кардиологического общества, нормативные значения WHR различаются для мужчин и женщин:
        </p>
        <div className="overflow-hidden bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-700 my-4">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-100 dark:bg-gray-600">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Пол</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Низкий WHR</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Нормальный WHR</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Высокий WHR</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-700 divide-y divide-gray-200 dark:divide-gray-600">
              <tr>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white">Женщины</td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">&lt; 0.75</td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">0.75 - 0.85</td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">&gt; 0.85</td>
              </tr>
              <tr>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white">Мужчины</td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">&lt; 0.90</td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">0.90 - 0.95</td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">&gt; 0.95</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p>
          Всемирная организация здравоохранения (ВОЗ) считает, что значения WHR более 0.85 для женщин и более 0.90 для мужчин указывают на абдоминальное ожирение и повышенный риск для здоровья.
        </p>

        <h3 className="flex items-center gap-2">
          <Users className="w-5 h-5 text-emerald-500" />
          Особенности интерпретации WHR для разных групп населения
        </h3>
        <p>
          Интерпретация значений WHR может иметь особенности в зависимости от возраста, этнической принадлежности и других факторов:
        </p>
        <ul>
          <li>
            <strong>Возрастные изменения:</strong> С возрастом соотношение талии к бедрам может естественным образом увеличиваться в связи с изменениями в распределении жира в организме. Однако это не означает, что повышенные значения WHR для пожилых людей не несут риски для здоровья.
          </li>
          <li>
            <strong>Этнические особенности:</strong> Существуют некоторые различия в оптимальных значениях WHR для разных этнических групп. Например, для населения Азии пороговые значения, указывающие на повышенный риск, могут быть ниже.
          </li>
          <li>
            <strong>Беременность:</strong> Во время беременности WHR не используется для оценки риска из-за физиологических изменений фигуры.
          </li>
          <li>
            <strong>Подростки:</strong> Для подростков интерпретация WHR должна учитывать возрастные и половые особенности развития.
          </li>
        </ul>

        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800 my-6">
          <h4 className="text-blue-700 dark:text-blue-300 font-medium">Соотношение талии к росту</h4>
          <p className="text-blue-600 dark:text-blue-400 text-sm mb-0">
            Помимо WHR, в последние годы все более популярным становится индекс соотношения талии к росту (WHtR). Согласно исследованиям, окружность талии не должна превышать половину роста человека (WHtR &lt; 0.5). Этот индекс может быть даже более точным предиктором риска сердечно-сосудистых заболеваний, чем WHR или ИМТ.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <BarChart className="w-5 h-5 text-emerald-500" />
          Методы измерения WHR
        </h3>
        <p>
          Для корректного расчета WHR важно правильно измерить окружность талии и бедер:
        </p>
        <ul>
          <li>
            <strong>Измерение талии:</strong> Измерение должно проводиться на уровне пупка или в самой узкой части туловища, обычно примерно на 2-3 см выше пупка. Мягкая измерительная лента должна располагаться горизонтально, не сдавливая кожу. Измерение проводится в конце нормального выдоха, когда человек расслаблен.
          </li>
          <li>
            <strong>Измерение бедер:</strong> Измерение проводится в самой широкой части ягодиц, обычно это на уровне тазобедренных суставов или чуть ниже. Лента должна проходить горизонтально, плотно прилегая к телу, но не сдавливая его.
          </li>
        </ul>
        <p>
          Измерения рекомендуется проводить утром, натощак, желательно всегда в одно и то же время дня для более точного сравнения результатов при мониторинге.
        </p>

        <h3 className="flex items-center gap-2">
          <Bell className="w-5 h-5 text-emerald-500" />
          Рекомендации по снижению WHR
        </h3>
        <p>
          Если ваш WHR превышает рекомендуемые значения, вот несколько научно обоснованных рекомендаций, которые могут помочь снизить этот показатель:
        </p>
        <ol>
          <li>
            <strong>Сбалансированное питание:</strong>
            <ul>
              <li>Увеличьте потребление овощей, фруктов, цельнозерновых продуктов, бобовых и нежирных источников белка.</li>
              <li>Ограничьте потребление простых углеводов (сахар, белый хлеб, сладости).</li>
              <li>Отдавайте предпочтение полезным жирам (оливковое масло, орехи, авокадо) вместо насыщенных и трансжиров.</li>
              <li>Контролируйте размер порций и общую калорийность рациона.</li>
              <li>Избегайте продуктов с высокой степенью промышленной обработки.</li>
            </ul>
          </li>
          <li>
            <strong>Регулярная физическая активность:</strong>
            <ul>
              <li>Комбинируйте кардио-нагрузки (ходьба, бег, плавание, велосипед) и силовые тренировки.</li>
              <li>Уделяйте особое внимание упражнениям для мышц кора (пресс, спина).</li>
              <li>Для снижения висцерального жира эффективны интервальные тренировки высокой интенсивности (HIIT).</li>
              <li>Выполняйте не менее 150 минут умеренной или 75 минут интенсивной физической активности в неделю, согласно рекомендациям Министерства здравоохранения РФ.</li>
            </ul>
          </li>
          <li>
            <strong>Здоровый образ жизни:</strong>
            <ul>
              <li>Высыпайтесь (7-8 часов полноценного сна).</li>
              <li>Управляйте стрессом (медитация, дыхательные практики, хобби).</li>
              <li>Ограничьте потребление алкоголя.</li>
              <li>Полностью откажитесь от курения.</li>
            </ul>
          </li>
          <li>
            <strong>Медицинское наблюдение:</strong>
            <ul>
              <li>Регулярно проходите профилактические осмотры.</li>
              <li>При необходимости обратитесь к эндокринологу для исключения гормональных нарушений, влияющих на распределение жира.</li>
              <li>Следите за показателями липидного профиля, уровнем глюкозы и артериальным давлением.</li>
            </ul>
          </li>
        </ol>

        <p className="font-semibold mt-6">
          Помните, что изменение WHR требует времени и комплексного подхода. Постепенное снижение значения этого индекса через здоровый образ жизни может значительно улучшить ваше здоровье и снизить риск развития многих заболеваний.
        </p>
      </div>
    </article>
  );
};

export default InfoContent;